/**
 * Changing avatars inside a world (play mode, desktop and VR).
 *
 * switchAvatar(): load the new model and its clips first (the current body
 * stays on screen meanwhile — never an empty spot), then persist the choice
 * (set_active_avatar, so the next session wears it too) and only then swap the
 * store's avatar in one update. That single update is what everything follows:
 *   - VRViewer's player remounts FixedRetargetedAvatar (keyed by URL) with
 *     carryPose, so you stay where you were; first-person eye height and the
 *     name tag come from the new avatar's own `animations` meta (__height…);
 *   - VRAvatarBody re-skins the headset body;
 *   - MultiplayerSync sends an immediate heartbeat with the new avatarUrl,
 *     look and animUrls (the new avatar's `animations` JSON), and peers swap
 *     the remote body, showing the loading ghost until it arrives.
 * A model that loads but then fails to render is caught by AvatarSwitchGuard,
 * which puts the previous avatar back (revertAvatarSwitch).
 *
 * "Get a new avatar" opens the existing create flow (AvatarCreateModal), so
 * gating, quotas, Turnstile and credits stay exactly as they are there.
 */
import { create } from 'zustand';
import { useGLTF } from '@react-three/drei';
import { isVrmUrl } from '../avatar/vrm/vrmFormat';
import { preloadVrm } from '../avatar/vrm/loadVrm';
import { fetchMyAvatars, setActiveAvatar, type Avatar } from './avatarService';
import { useWorldStore } from '../stores/worldStore';
import { broadcastEmote } from './worldBroadcast';
import { getCurrentWorldId } from './realtimeManager';

interface Snapshot { id: string | null; model_url: string; animations: Record<string, string>; active_texture_url: string | null }

interface AvatarSwitchState {
  /** The wardrobe panel (2D overlay, or the in-headset panel). */
  pickerOpen: boolean;
  /** The create flow, opened from the wardrobe's "Get a new avatar". */
  createOpen: boolean;
  avatars: Avatar[] | null;
  error: string | null;
  /** Avatar being changed into (loading). */
  switchingId: string | null;
  toast: string | null;
  setPickerOpen: (open: boolean) => void;
  setCreateOpen: (open: boolean) => void;
  showToast: (t: string) => void;
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;

export const useAvatarSwitch = create<AvatarSwitchState>((set) => ({
  pickerOpen: false,
  createOpen: false,
  avatars: null,
  error: null,
  switchingId: null,
  toast: null,
  setPickerOpen: (pickerOpen) => {
    set({ pickerOpen });
    if (pickerOpen) void refreshMyAvatars();
  },
  setCreateOpen: (createOpen) => set(createOpen ? { createOpen, pickerOpen: false } : { createOpen }),
  showToast: (toast) => {
    clearTimeout(toastTimer);
    set({ toast });
    toastTimer = setTimeout(() => set({ toast: null }), 4200);
  },
}));

export const openAvatarPicker = () => useAvatarSwitch.getState().setPickerOpen(true);
export const closeAvatarPicker = () => useAvatarSwitch.getState().setPickerOpen(false);
export const openAvatarCreate = () => useAvatarSwitch.getState().setCreateOpen(true);

export async function refreshMyAvatars(): Promise<void> {
  useAvatarSwitch.setState({ error: null });
  try {
    const avatars = await fetchMyAvatars();
    useAvatarSwitch.setState({ avatars });
  } catch (e) {
    console.warn('[AvatarSwitch] could not load avatars', e);
    useAvatarSwitch.setState({ avatars: useAvatarSwitch.getState().avatars || [], error: 'Couldn’t load your avatars. Check your connection.' });
  }
}

/** Card picture for an avatar: the worn look's render, else its thumbnail. */
export function avatarPicture(a: Avatar): string | null {
  if (a.active_texture_url && a.active_look?.thumbnail_url) return a.active_look.thumbnail_url;
  return a.thumbnail_url && !a.thumbnail_url.includes('meshy.ai') ? a.thumbnail_url : null;
}

/** Is this the avatar being worn right now? */
export function isWorn(a: Avatar): boolean {
  const s = useWorldStore.getState();
  return s.activeAvatarId ? s.activeAvatarId === a.id : s.avatarUrl === a.model_url;
}

// ---- Loading ----

/** Load a GLB into the same cache the avatar components read (useGLTF), so
 *  the swap renders without suspending. Rejects if it won't load. */
async function loadGLTF(url: string): Promise<void> {
  // VRMs load per avatar instance (avatar/vrm), not through useGLTF's cache.
  if (isVrmUrl(url)) return preloadVrm(url);
  for (let i = 0; i < 4; i++) {
    try {
      // useGLTF is a plain suspend-cache read here (no React hooks inside):
      // it throws the pending promise until the model is parsed, then returns.
      useGLTF(url);
      return;
    } catch (thrown) {
      if (thrown && typeof (thrown as Promise<unknown>).then === 'function') await (thrown as Promise<unknown>);
      else throw thrown;
    }
  }
  useGLTF(url);
}

/** URLs the avatar components load for this avatar (model + its own clips). */
function assetUrls(a: Avatar): string[] {
  const anims = a.animations || {};
  const clips = ['idle', 'walking', 'running', 'jumping'].map((k) => anims[k]).filter((u): u is string => typeof u === 'string' && /^https?:|^\//.test(u));
  return [a.model_url, ...new Set(clips)];
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timed out')), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

// ---- Switching ----

let previous: Snapshot | null = null;
let lastSwitchUrl: string | null = null;

function snapshot(): Snapshot | null {
  const s = useWorldStore.getState();
  if (!s.avatarUrl || s.avatarUrl === 'skip') return null;
  return { id: s.activeAvatarId, model_url: s.avatarUrl, animations: s.avatarAnimations || {}, active_texture_url: s.avatarLookTextureUrl };
}

/** Stop an emote (it was fitted to the old rig) here and for everyone else. */
function stopEmote() {
  const s = useWorldStore.getState();
  if (!s.localEmote) return;
  s.stopLocalEmote();
  const wid = s.actualWorldId || getCurrentWorldId();
  if (wid) broadcastEmote(wid, null);
}

const shortName = (n: string) => (n.length > 28 ? `${n.slice(0, 27)}…` : n);

/** Change into one of your avatars without leaving the world. */
export async function switchAvatar(a: Avatar): Promise<boolean> {
  const st = useAvatarSwitch.getState();
  if (st.switchingId) return false;
  if (isWorn(a)) return true;
  useAvatarSwitch.setState({ switchingId: a.id });
  try {
    try {
      await withTimeout(Promise.all(assetUrls(a).map(loadGLTF)), 90_000);
    } catch (e) {
      console.warn('[AvatarSwitch] model failed to load', a.model_url, e);
      for (const u of assetUrls(a)) { try { useGLTF.clear(u); } catch { /* not cached */ } }
      st.showToast(`Couldn’t load “${shortName(a.name)}”. You’re still wearing your current avatar.`);
      return false;
    }
    try {
      await setActiveAvatar(a.id);
    } catch (e) {
      console.warn('[AvatarSwitch] set_active_avatar failed', e);
      st.showToast('Couldn’t change avatars right now. Try again in a moment.');
      return false;
    }
    previous = snapshot();
    lastSwitchUrl = a.model_url;
    // One store update: stop the old rig's emote and put on the new avatar.
    stopEmote();
    useWorldStore.getState().setAvatarFromDb(a);
    useAvatarSwitch.setState((s) => ({
      pickerOpen: false,
      avatars: s.avatars?.map((x) => ({ ...x, is_active: x.id === a.id })) ?? s.avatars,
    }));
    st.showToast(`You’re now ${shortName(a.name)}`);
    return true;
  } finally {
    useAvatarSwitch.setState({ switchingId: null });
  }
}

/** The avatar just switched to crashed while rendering: put the old one back. */
export function revertAvatarSwitch(failedUrl: string, error?: unknown) {
  console.warn('[AvatarSwitch] avatar failed to render', failedUrl, error);
  try { useGLTF.clear(failedUrl); } catch { /* not cached */ }
  const prev = previous;
  if (!prev || lastSwitchUrl !== failedUrl || prev.model_url === failedUrl) return false;
  previous = null;
  lastSwitchUrl = null;
  useWorldStore.getState().setAvatarFromDb({ id: prev.id || '', model_url: prev.model_url, animations: prev.animations, active_texture_url: prev.active_texture_url });
  if (prev.id) {
    useWorldStore.setState({ activeAvatarId: prev.id });
    setActiveAvatar(prev.id).catch((e) => console.warn('[AvatarSwitch] could not restore active avatar', e));
  } else {
    useWorldStore.setState({ activeAvatarId: null });
  }
  useAvatarSwitch.setState((s) => ({ avatars: s.avatars?.map((x) => ({ ...x, is_active: x.id === prev.id })) ?? s.avatars }));
  useAvatarSwitch.getState().showToast('That avatar couldn’t be shown, so you’re back in your previous one.');
  return true;
}
